import React, { useState, useEffect, useRef } from "react";
import T from '../../components/common/T';
import { useNavigate, useParams } from "react-router-dom";
import { useWorkforce } from "../../context/WorkforceContext";
import { toast } from "react-toastify";
import { toInputDate } from "../../utils/dateUtils";
import { 
  UserGroupIcon, 
  ArrowLeftIcon,
  UserIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from "@heroicons/react/24/outline";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

const WorkforceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addWorkforce, updateWorkforce, getWorkforceById } = useWorkforce();
  const isEditing = Boolean(id);
  const loadedIdRef = useRef(null);

  const [isDirty, setIsDirty] = useState(false);
const { customData, setCustomData, saveCustomFields } = useCustomFields('workforce', id ?? null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Info
    employee_id: "",
    name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    gender: "",
    
    // Address
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    
    // Employment Details
    role: "",
    department: "",
    employment_type: "Full-Time",
    date_of_joining: new Date().toISOString().split('T')[0],
    reporting_to: "",
    
    // Skills
    primary_skill: "",
    secondary_skills: "",
    certifications: "",
    experience_years: "",
    
    // Compensation
    hourly_wage: "",
    monthly_salary: "",
    currency: "INR",
    
    // Status
    status: "Active",
    
    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relation: "",
    
    // Notes
    notes: ""
  });

  // Load data for editing
  useEffect(() => {
    if (!isEditing || !id) return;
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getWorkforceById(id);
        if (data) {
          setFormData({
            employee_id: data.employee_id || "",
            name: data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            date_of_birth: toInputDate(data.date_of_birth),
            gender: data.gender || "",
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            postal_code: data.postal_code || "",
            country: data.country || "India",
            role: data.role || "",
            department: data.department || "",
            employment_type: data.employment_type || "Full-Time",
            date_of_joining: toInputDate(data.date_of_joining),
            reporting_to: data.reporting_to || "",
            primary_skill: data.primary_skill || "",
            secondary_skills: data.secondary_skills || "",
            certifications: data.certifications || "",
            experience_years: data.experience_years || "",
            hourly_wage: data.hourly_wage || "",
            monthly_salary: data.monthly_salary || "",
            currency: data.currency || "INR",
            status: data.status || "Active",
            emergency_contact_name: data.emergency_contact_name || "",
            emergency_contact_phone: data.emergency_contact_phone || "",
            emergency_contact_relation: data.emergency_contact_relation || "",
            notes: data.notes || ""
          });
        }
      } catch (err) {
        toast.error("Could not load employee details");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditing, getWorkforceById]);

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await updateWorkforce(id, formData);
      } else {
        await addWorkforce(formData);
      }
      setIsDirty(false);
      setTimeout(() => navigate("/admin/workforce"), 800);
    } catch (err) {
      // Error handling done in context
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        
        <div className="max-w-6xl mx-auto">
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-8 py-6 text-white flex items-center gap-4">
              <button 
                onClick={() => navigate("/admin/workforce")} 
                className="hover:bg-gray-700 p-2 rounded-lg transition"
              >
                <ArrowLeftIcon className="w-6 h-6"/>
              </button>
              <div className="flex-1">
                <h1 className="text-base font-bold flex items-center gap-2">
                  <UserGroupIcon className="w-7 h-7 text-blue-400" />
                  {isEditing ? "Edit Employee Profile" : "Onboard New Employee"}
                </h1>
                <p className="text-gray-300 text-sm mt-1">
                  {isEditing ? "Update employee information" : "Add a new team member to your workforce"}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8">
              {/* Personal Information Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-800"><T>Personal Information</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Employee ID *
                    </label>
                    <input 
                      type="text" 
                      name="employee_id" 
                      value={formData.employee_id} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="EMP-001" 
                      required 
                      disabled={isEditing}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="John Doe" 
                      required 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input 
                      type="date" 
                      name="date_of_birth" 
                      value={formData.date_of_birth} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Gender
                    </label>
                    <select 
                      name="gender" 
                      value={formData.gender} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value=""><T>Select Gender</T></option>
                      <option value="Male"><T>Male</T></option>
                      <option value="Female"><T>Female</T></option>
                      <option value="Other"><T>Other</T></option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                      <EnvelopeIcon className="w-4 h-4" /> Email *
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="john@company.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                      <PhoneIcon className="w-4 h-4" /> Phone
                    </label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="+91-9876543210"
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <MapPinIcon className="w-5 h-5 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-800"><T>Address</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Street Address
                    </label>
                    <input 
                      type="text" 
                      name="address" 
                      value={formData.address} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="123, Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">City</label>
                    <input 
                      type="text" 
                      name="city" 
                      value={formData.city} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Mumbai"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">State</label>
                    <input 
                      type="text" 
                      name="state" 
                      value={formData.state} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Maharashtra"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Postal Code</label>
                    <input 
                      type="text" 
                      name="postal_code" 
                      value={formData.postal_code} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="400001"
                    />
                  </div>
                </div>
              </div>

              {/* Employment Details Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <BriefcaseIcon className="w-5 h-5 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-800"><T>Employment Details</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Role / Designation *</label>
                    <input 
                      type="text" 
                      name="role" 
                      value={formData.role} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="e.g., Site Engineer"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Department</label>
                    <input 
                      type="text" 
                      name="department" 
                      value={formData.department} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="e.g., Construction"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Employment Type</label>
                    <select 
                      name="employment_type" 
                      value={formData.employment_type} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="Full-Time"><T>Full-Time</T></option>
                      <option value="Part-Time"><T>Part-Time</T></option>
                      <option value="Contract"><T>Contract</T></option>
                      <option value="Daily-Wage"><T>Daily Wage</T></option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Date of Joining</label>
                    <input 
                      type="date" 
                      name="date_of_joining" 
                      value={formData.date_of_joining} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Reporting To</label>
                    <input 
                      type="text" 
                      name="reporting_to" 
                      value={formData.reporting_to} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Manager name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
                    <select 
                      name="status" 
                      value={formData.status} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="Active"><T>Active</T></option>
                      <option value="On Leave"><T>On Leave</T></option>
                      <option value="Inactive"><T>Inactive</T></option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Experience (Years)
                    </label>
                    <input 
                      type="number" 
                      step="0.5"
                      name="experience_years" 
                      value={formData.experience_years} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="5.5" 
                    />
                  </div>
                </div>
              </div>

              {/* Skills Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <h2 className="text-base font-bold text-gray-800"><T>Skills & Certifications</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Primary Skill
                    </label>
                    <input 
                      type="text" 
                      name="primary_skill" 
                      value={formData.primary_skill} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="e.g., Electrical Engineering" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Secondary Skills
                    </label>
                    <input 
                      type="text" 
                      name="secondary_skills" 
                      value={formData.secondary_skills} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Comma-separated" 
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Certifications
                    </label>
                    <textarea 
                      name="certifications" 
                      value={formData.certifications} 
                      onChange={handleChange} 
                      rows="2"
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="List any professional certifications"
                    />
                  </div>
                </div>
              </div>

              {/* Compensation Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <CurrencyRupeeIcon className="w-5 h-5 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-800"><T>Compensation</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Hourly Wage (₹)
                    </label>
                    <input 
                      type="number" 
                      step="0.01"
                      name="hourly_wage" 
                      value={formData.hourly_wage} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="350.00" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Monthly Salary (₹)
                    </label>
                    <input 
                      type="number" 
                      step="0.01"
                      name="monthly_salary" 
                      value={formData.monthly_salary} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="70000.00" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Currency
                    </label>
                    <select 
                      name="currency" 
                      value={formData.currency} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                    >
                      <option value="INR"><T>INR (₹)</T></option>
                      <option value="USD"><T>USD ($)</T></option>
                      <option value="EUR"><T>EUR (€)</T></option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                  <h2 className="text-base font-bold text-gray-800"><T>Emergency Contact</T></h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Contact Name
                    </label>
                    <input 
                      type="text" 
                      name="emergency_contact_name" 
                      value={formData.emergency_contact_name} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Jane Doe" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input 
                      type="tel" 
                      name="emergency_contact_phone" 
                      value={formData.emergency_contact_phone} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="+91-9876543210" 
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Relationship
                    </label>
                    <input 
                      type="text" 
                      name="emergency_contact_relation" 
                      value={formData.emergency_contact_relation} 
                      onChange={handleChange} 
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                      placeholder="Spouse" 
                    />
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="mb-8">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea 
                  name="notes" 
                  value={formData.notes} 
                  onChange={handleChange} 
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" 
                  placeholder="Any additional information..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6 border-t">
                <button 
                  type="button" 
                  onClick={() => navigate("/admin/workforce")} 
                  className="px-8 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
                  disabled={loading}
                >
                  Cancel
                </button>
                <CustomFieldRenderer module="workforce" customData={customData} onChange={setCustomData} />
            <button 
                  type="submit" 
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-lg hover:from-blue-700 hover:to-blue-800 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (isEditing ? 'Update Profile' : 'Save & Onboard')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default WorkforceForm;